import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { StyleSpecification } from "maplibre-gl";

import { MAP_STYLES } from "@/constants/map";
import { MapProvider, MapSource, SelectedFeature } from "@/types/map";

type MapStyleKey = keyof typeof MAP_STYLES;

export type LegendFilterType =
  | string
  | { min: number | null; max: number | null }
  | null;

// Snapshot of the active time-series frame per dataset. Read by useLegendFilter
// so the legend toggle can compose its filter with the current time filter.
export type TimeSeriesGroupByFilter = {
  time: string;
  attribute: string;
};

export interface MapState {
  selectedFeature: SelectedFeature | null;
  comparisonFeatureData: Array<SelectedFeature>;
  draw: {
    popupInfo: {
      lngLat: [number, number];
    } | null;
    feature: GeoJSON.Feature | null;
  };
  legendFilters?: { [datasetId: string]: LegendFilterType[] };
  // Current time-frame filter per dataset, used to AND with legend filters.
  timeSeriesGroupByFilter?: { [datasetId: string]: TimeSeriesGroupByFilter };
  baseMap: {
    title: string;
    description: string;
    value: StyleSpecification | string;
    provider: MapProvider;
    thumbnail?: string;
  };
  mapSource: MapSource;
}

export const mapInitialState: MapState = {
  selectedFeature: null,
  comparisonFeatureData: [],
  draw: {
    popupInfo: null,
    feature: null
  },
  legendFilters: undefined,
  timeSeriesGroupByFilter: undefined,
  baseMap: MAP_STYLES.street,
  mapSource: MapSource.STYLE_URL
};

export const mapSlice = createSlice({
  name: "map",
  initialState: mapInitialState,
  reducers: {
    setSelectedFeature: (
      state,
      action: PayloadAction<SelectedFeature | null>
    ) => {
      state.selectedFeature = action.payload;
    },
    setComparisonFeature: (
      state,
      action: PayloadAction<Array<SelectedFeature>>
    ) => {
      state.comparisonFeatureData = action.payload;
    },
    setLegendFilters: (
      state,
      action: PayloadAction<
        { [datasetId: string]: LegendFilterType[] } | undefined
      >
    ) => {
      state.legendFilters = action.payload;
    },
    setTimeSeriesGroupByFilter: (
      state,
      action: PayloadAction<
        { [datasetId: string]: TimeSeriesGroupByFilter } | undefined
      >
    ) => {
      state.timeSeriesGroupByFilter = action.payload;
    },
    removeComparisonFeatureByIndex: (state, action: PayloadAction<number>) => {
      state.comparisonFeatureData.splice(action.payload, 1);
    },
    clearComparisonFeature: (state) => {
      state.comparisonFeatureData = [];
    },
    addComparisonFeature: (state, action: PayloadAction<SelectedFeature>) => {
      state.comparisonFeatureData = [
        ...state.comparisonFeatureData,
        action.payload
      ];
    },
    setMapStyle: (state, action: PayloadAction<MapStyleKey>) => {
      const selectedBaseMap = MAP_STYLES[action.payload];

      // @ts-expect-error warning not clear
      state.baseMap = selectedBaseMap;

      if (selectedBaseMap.provider === MapProvider.MAPBOX)
        state.mapSource = MapSource.MAPBOX;
      else state.mapSource = MapSource.STYLE_URL;
    },
    setMapSource: (
      state,
      action: PayloadAction<MapSource.MAPBOX | MapSource.STYLE_URL>
    ) => {
      state.mapSource = action.payload;
    },
    resetMapState: () => ({ ...mapInitialState }),
    setMapState: (_state, action: PayloadAction<MapState>) => {
      return action.payload;
    }
  }
});

export const {
  setSelectedFeature,
  setComparisonFeature,
  setLegendFilters,
  setTimeSeriesGroupByFilter,
  addComparisonFeature,
  clearComparisonFeature,
  removeComparisonFeatureByIndex,
  setMapStyle,
  resetMapState,
  setMapState
} = mapSlice.actions;

export const mapReducer = mapSlice.reducer;
